/**
 * Módulo SST — a tela do técnico de segurança (18/09/2026).
 *
 * Primeira versão, feita em cima do que as cargas já trazem sozinhas: o cadastro
 * do Domínio (todo dia 12:00) e os ASOs do portal da ASMETRO (todo dia 06:30).
 * O relógio do backend compara com a norma e o PCMSO e entrega as pendências; a
 * tela só organiza por urgência:
 *
 *   1. Agir agora   — gente trabalhando com exame vencido, faltando, ou que
 *                     voltou de afastamento sem exame de retorno;
 *   2. Programar    — vence dentro de 30 dias, ou volta com data marcada;
 *   3. Afastados    — o exame se faz na volta; está aqui para não sumir.
 *
 * A aba "Nomes sem vínculo" existe porque o portal não tem CPF: o que não casou
 * por nome fica esperando o técnico dizer quem é. Nada é adivinhado.
 */

import { api, ErroApi } from '../api.js';
import { avisar } from '../comum/aviso.js';
import { escapar, quando } from '../comum/formato.js';
import { montarFichaSst } from './sst_ficha.js';

interface Referencia {
  chave: string;
  motivo: string;
  afastado?: boolean;
  estimada?: boolean;
  data_exame?: string;
  inicio?: string;
  fim?: string | null;
  dias?: number;
  causa?: string;
  ultima?: string;
  desligamento?: string;
  base?: string;
}
interface Pendencia {
  id: number;
  funcionario_id: number;
  nome: string;
  funcao: string | null;
  ghe: string | null;
  obra: string | null;
  referencia: Referencia;
  vence_em: string | null;
  ativo: boolean;
}
interface Ativo {
  id: number;
  nome: string;
  funcao: string | null;
  ghe: string | null;
  ghe_nome: string | null;
  obra: string | null;
  admissao: string | null;
  cnh_categoria: string | null;
  aso_data: string | null;
  aso_tipo: string | null;
  aso_validade: string | null;
  asos: number;
  afastado: string | null;
}
interface NaFila {
  id: number;
  nome_portal: string;
  nome_normalizado: string;
  funcao: string | null;
  nascimento: string | null;
  exames: number;
  primeiro_exame: string | null;
  ultimo_exame: string | null;
  motivo: string;
  sugestao_funcionario_id: number | null;
  sugestao_nome: string | null;
  sugestao_nascimento: string | null;
}
interface DocumentoBase {
  id: number;
  tipo: string;
  titulo: string;
  fornecedor: string | null;
  responsavel: string | null;
  registro: string | null;
  vigencia_inicio: string;
  vigencia_fim: string | null;
  obs: string | null;
  auditoria_situacao: 'nao_auditado' | 'com_ressalvas' | 'sem_ressalvas' | 'substituido';
  auditado_em: string | null;
  auditoria_ref: string | null;
  reanalisar_na_revisao: boolean;
  achados_abertos: number;
  achados_altos: number;
  depende_de: { titulo: string; vigencia_fim: string | null; motivo: string }[] | null;
}
interface AchadoAuditoria {
  codigo: string;
  gravidade: 'alta' | 'media' | 'baixa';
  erro: string;
  norma: string | null;
  correcao_sugerida: string | null;
  acao_erp: string | null;
  situacao: string;
  situacao_obs: string | null;
  reanalisar: boolean;
  documento: string | null;
  tambem_em: string | null;
}
interface LinhaMatriz {
  codigo: string;
  nome: string;
  carga_inicial_h: number | null;
  carga_periodica_h: number | null;
  validade_meses: number | null;
  validade_por_norma: boolean;
  fonte: string | null;
  conferido_em: string | null;
  alvo: string;
  exigencia: string;
  procedencia: string;
  reanalisar: boolean;
  doc_tipo: string;
  expostos: number;
}
interface Painel {
  hoje: string;
  pendencias: Pendencia[];
  ativos: Ativo[];
  fila: NaFila[];
  documentos: DocumentoBase[];
  achados: AchadoAuditoria[];
  matriz: LinhaMatriz[];
  reanalise: { exames: number; treinamentos: number; ghes: number };
  cargas: Record<'dominio' | 'asmetro' | 'relogio', { em: string } | null>;
}
interface Achado {
  id: number;
  nome: string;
  nascimento: string | null;
  admissao: string | null;
  desligamento: string | null;
  funcao: string | null;
}

type Aba = 'pendencias' | 'funcionarios' | 'fila' | 'documentos' | 'cargas';

const ROTULO: Record<string, string> = {
  vencido: 'ASO vencido',
  a_vencer: 'ASO vencendo',
  sem_aso: 'Sem ASO no portal',
  retorno_previsto: 'Exame de retorno ao voltar',
  retorno_atrasado: 'Voltou sem exame de retorno',
  toxicologico_sem_registro: 'Toxicológico sem registro',
  toxicologico_vencido: 'Toxicológico vencido',
  toxicologico_a_vencer: 'Toxicológico vencendo',
  demissional_pendente: 'Demissional pendente',
};
const CAUSA: Record<string, string> = { doenca: 'doença', acidente: 'acidente', maternidade: 'maternidade', aposentadoria_invalidez: 'aposent. invalidez', outro: 'outro' };

function dataBr(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

function dias(de: string, ate: string): number {
  return Math.round((Date.parse(`${ate}T12:00:00Z`) - Date.parse(`${de}T12:00:00Z`)) / 86_400_000);
}

/** 1 = agir agora, 2 = programar, 3 = afastado (exame na volta). */
function urgencia(p: Pendencia): 1 | 2 | 3 {
  const r = p.referencia;
  if (r.motivo === 'retorno_atrasado' || r.motivo === 'demissional_pendente') return 1;
  if (r.motivo === 'retorno_previsto') return r.fim ? 2 : 3;
  if (r.afastado) return 3;
  if (r.motivo === 'a_vencer' || r.motivo === 'toxicologico_a_vencer') return 2;
  return 1;
}

export function montarSst(raiz: HTMLElement): void {
  let dados: Painel | null = null;
  let aba: Aba = 'pendencias';
  let busca = '';
  /** A ficha aberta (03/11): clicou num nome, a tela inteira vira a ficha dele. */
  let ficha: number | null = null;

  raiz.innerHTML = `<p class="carregando">Carregando o módulo SST…</p>`;
  // Um ouvinte só, no container: os nomes são `<button data-ficha>` em
  // qualquer aba, e `desenhar()` troca o innerHTML inteiro a cada vez.
  raiz.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-ficha]');
    if (!b) return;
    ficha = Number(b.dataset['ficha']);
    desenhar();
  });

  async function carregar(): Promise<void> {
    try {
      dados = await api.get<Painel>('/api/sst/painel');
      desenhar();
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(erro instanceof ErroApi ? erro.message : 'Não consegui carregar o módulo SST.')}</p>`;
    }
  }

  function desenhar(): void {
    if (!dados) return;
    if (ficha !== null) {
      montarFichaSst(raiz, ficha, () => {
        ficha = null;
        // Volta com o painel atualizado: a ficha pode ter mudado algo (documento novo).
        void carregar();
      });
      return;
    }
    const d = dados;
    const agir = d.pendencias.filter((p) => urgencia(p) === 1);
    const programar = d.pendencias.filter((p) => urgencia(p) === 2);
    const afastados = d.ativos.filter((a) => a.afastado);
    const kpi = (rotulo: string, valor: number, classe = ''): string =>
      `<div class="kpi ${valor > 0 ? classe : ''}"><span class="kpi-rotulo">${rotulo}</span><span class="kpi-valor">${valor}</span></div>`;

    raiz.innerHTML = `
      <div class="kpis">
        ${kpi('Agir agora', agir.length, 'alerta')}
        ${kpi('Programar (30 dias)', programar.length, 'aviso')}
        ${kpi('Ativos', d.ativos.length)}
        ${kpi('Afastados', afastados.length)}
        ${kpi('Nomes sem vínculo', d.fila.length, 'aviso')}
      </div>
      <nav class="abas abas-2" id="sst-abas">
        ${(
          [
            ['pendencias', `🩺 Pendências <span class="aba-badge">${d.pendencias.length}</span>`],
            ['funcionarios', '👷 Funcionários e ASO'],
            ['fila', `🔗 Nomes sem vínculo <span class="aba-badge">${d.fila.length}</span>`],
            ['documentos', `📑 Documentos e lei <span class="aba-badge">${d.achados.filter((a) => a.situacao !== 'corrigido').length}</span>`],
            ['cargas', '🔄 Cargas'],
          ] as [Aba, string][]
        )
          .map(([c, t]) => `<button class="aba" data-aba="${c}" aria-current="${c === aba}">${t}</button>`)
          .join('')}
      </nav>
      <div id="sst-secao"></div>`;

    raiz.querySelector('#sst-abas')!.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-aba]');
      if (!b) return;
      aba = b.dataset['aba'] as Aba;
      busca = '';
      desenhar();
    });

    const secao = raiz.querySelector<HTMLElement>('#sst-secao')!;
    if (aba === 'pendencias') secaoPendencias(secao, d);
    else if (aba === 'funcionarios') secaoFuncionarios(secao, d);
    else if (aba === 'fila') secaoFila(secao, d);
    else if (aba === 'documentos') secaoDocumentos(secao, d);
    else secaoCargas(secao, d);
  }

  function campoBusca(secao: HTMLElement, dica: string, aoMudar: () => void): string {
    queueMicrotask(() => {
      const i = secao.querySelector<HTMLInputElement>('#sst-busca');
      i?.addEventListener('input', () => {
        busca = i.value.trim().toUpperCase();
        aoMudar();
      });
    });
    return `<div class="barra-busca"><input id="sst-busca" type="search" placeholder="${escapar(dica)}" autocomplete="off" value="${escapar(busca)}"></div>`;
  }

  // ---------------------------------------------------------------- pendências
  function oQue(p: Pendencia, hoje: string): string {
    const r = p.referencia;
    const rotulo = ROTULO[r.motivo] ?? r.motivo;
    let detalhe = '';
    if (r.motivo === 'vencido' && p.vence_em) detalhe = `venceu em ${dataBr(p.vence_em)} (há ${dias(p.vence_em, hoje)} dias)${r.estimada ? ' — validade estimada em 12 meses' : ''}`;
    else if (r.motivo === 'a_vencer' && p.vence_em) detalhe = `vence em ${dataBr(p.vence_em)} (${dias(hoje, p.vence_em)} dias)${r.estimada ? ' — estimada' : ''}`;
    else if (r.motivo === 'sem_aso') detalhe = 'nenhum ASO deste nome no portal da ASMETRO; pode ser grafia diferente (ver "Nomes sem vínculo")';
    else if (r.motivo === 'retorno_atrasado') detalhe = `afastado ${r.dias} dias por ${CAUSA[r.causa ?? ''] ?? r.causa}; voltou em ${dataBr(p.vence_em)} e não há ASO depois disso`;
    else if (r.motivo === 'retorno_previsto') detalhe = `afastado desde ${dataBr(r.inicio)} por ${CAUSA[r.causa ?? ''] ?? r.causa}${r.fim ? `; volta prevista em ${dataBr(p.vence_em)}` : '; sem data de volta'}`;
    else if (r.motivo.startsWith('toxicologico')) detalhe = r.ultima ? `última coleta em ${dataBr(r.ultima)}` : 'motorista sem nenhum toxicológico registrado no sistema';
    else if (r.motivo === 'demissional_pendente') detalhe = `desligado em ${dataBr(r.desligamento)}; prazo ${dataBr(p.vence_em)}`;
    const classe = urgencia(p) === 1 ? 'v-vencido' : urgencia(p) === 2 ? 'v-atencao' : '';
    return `<b class="${classe}">${escapar(rotulo)}</b><br><span class="sub-inline">${escapar(detalhe)}${r.afastado && !r.motivo.startsWith('retorno') ? ' · está afastado' : ''}</span>`;
  }

  function tabelaPendencias(lista: Pendencia[], hoje: string): string {
    if (!lista.length) return `<p class="dica">Nada aqui. 👌</p>`;
    // Sem coluna "Base": o texto legal é longo e, numa célula que não quebra, empurrava a tabela
    // para fora da tela (barra horizontal). Vai como linha miúda embaixo do "o quê".
    return `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr>
        <th>Funcionário</th><th>Função · GHE</th><th>Obra</th><th>O quê</th></tr></thead><tbody>
      ${lista
        .map(
          (p) => `<tr><td><button class="sst-link" type="button" data-ficha="${p.funcionario_id}" title="Abrir a ficha">${escapar(p.nome)}</button></td>
            <td>${escapar(p.funcao ?? '—')}${p.ghe ? ` <span class="sub-inline">GHE ${escapar(p.ghe)}</span>` : ''}</td>
            <td class="comp">${escapar(p.obra ?? (p.ativo ? '—' : 'desligado'))}</td>
            <td>${oQue(p, hoje)}${p.referencia.base ? `<br><span class="sub-inline">Base: ${escapar(p.referencia.base)}</span>` : ''}</td></tr>`,
        )
        .join('')}
      </tbody></table></div>`;
  }

  function secaoPendencias(secao: HTMLElement, d: Painel): void {
    const filtra = (p: Pendencia): boolean => !busca || `${p.nome} ${p.funcao ?? ''} ${p.obra ?? ''}`.toUpperCase().includes(busca);
    const pinta = (): void => {
      const grupos: [string, string, Pendencia[]][] = [
        ['🚨 Agir agora', 'Gente trabalhando com exame vencido ou faltando, quem voltou de afastamento longo sem exame de retorno, e o que a lei manda para motorista e demissão.', d.pendencias.filter((p) => urgencia(p) === 1 && filtra(p))],
        ['📅 Programar', 'Vence nos próximos 30 dias, ou volta de afastamento com data marcada (o exame é antes de reassumir).', d.pendencias.filter((p) => urgencia(p) === 2 && filtra(p))],
        ['🛌 Afastados — exame na volta', 'Não dá para examinar quem está afastado. Ficam aqui para não sumirem: no dia em que o Domínio mostrar a volta, sobem para "Agir agora".', d.pendencias.filter((p) => urgencia(p) === 3 && filtra(p))],
      ];
      alvo.innerHTML = grupos
        .map(([titulo, dica, lista]) => `<section class="ped-painel"><h3>${titulo} <span class="sub-inline">${lista.length}</span></h3><p class="dica">${dica}</p>${tabelaPendencias(lista, d.hoje)}</section>`)
        .join('');
    };
    secao.innerHTML = `<p class="dica"><b>Regra em vigor (decisão do Lucas, 18/09/2026):</b> a lei prevalece sobre os documentos até a revisão chegar.
        Onde o PCMSO é mais rigoroso que a lei, vale o PCMSO. ${d.reanalise.exames} exigências de exame e ${d.reanalise.ghes} GHEs vêm de documentos auditados
        com ressalvas e estão marcados para reanálise (aba “Documentos e lei”).</p>
      ${campoBusca(secao, 'Buscar por nome, função ou obra…', () => pinta())}<div id="sst-lista"></div>`;
    const alvo = secao.querySelector<HTMLElement>('#sst-lista')!;
    pinta();
  }

  // ---------------------------------------------------------- documentos e lei
  function secaoDocumentos(secao: HTMLElement, d: Painel): void {
    const regra = d.documentos.find((x) => x.tipo === 'interno');
    const auditaveis = d.documentos.filter((x) => !['interno', 'norma', 'lei'].includes(x.tipo));
    const SITUACAO: Record<string, string> = { com_ressalvas: 'com ressalvas', sem_ressalvas: 'sem ressalvas', substituido: 'substituído', nao_auditado: 'não auditado' };
    const GRAV: Record<string, { rotulo: string; classe: string }> = { alta: { rotulo: 'ALTA', classe: 'v-vencido' }, media: { rotulo: 'MÉDIA', classe: 'v-atencao' }, baixa: { rotulo: 'BAIXA', classe: '' } };

    const alertas = (doc: DocumentoBase): string => {
      const a: string[] = [];
      if (doc.vigencia_fim && doc.auditoria_situacao !== 'substituido') {
        const faltam = dias(d.hoje, doc.vigencia_fim);
        if (faltam < 0) a.push(`<span class="v-vencido">vencido há ${-faltam} dias</span>`);
        else if (faltam <= 60) a.push(`<span class="v-atencao">revisão em ${faltam} dias</span>`);
      }
      if (!doc.vigencia_fim && doc.tipo === 'ltcat') a.push(`<span class="v-atencao">sem validade no documento; revisão sugerida em ${dataBr(`${Number(doc.vigencia_inicio.slice(0, 4)) + 1}${doc.vigencia_inicio.slice(4)}`)}</span>`);
      for (const p of doc.depende_de ?? []) {
        if (p.vigencia_fim && doc.vigencia_fim && p.vigencia_fim < doc.vigencia_fim) a.push(`<span class="v-atencao">depende de “${escapar(p.titulo)}”, que vence antes (${dataBr(p.vigencia_fim)})</span>`);
      }
      return a.join('<br>');
    };

    const pintaAchados = (): void => {
      const filtroDoc = secao.querySelector<HTMLSelectElement>('#sst-fdoc')?.value ?? '';
      const filtroGrav = secao.querySelector<HTMLSelectElement>('#sst-fgrav')?.value ?? '';
      const lista = d.achados.filter(
        (a) =>
          (!filtroDoc || (a.documento ?? 'entre') === filtroDoc || a.tambem_em === filtroDoc) &&
          (!filtroGrav || a.gravidade === filtroGrav) &&
          (!busca || `${a.codigo} ${a.erro} ${a.norma ?? ''} ${a.acao_erp ?? ''}`.toUpperCase().includes(busca)),
      );
      secao.querySelector<HTMLElement>('#sst-achados')!.innerHTML = lista.length
        ? lista
            .map(
              (a) => `<details style="border-bottom:1px solid var(--linha);padding:.5rem 0">
                <summary style="cursor:pointer"><b>${escapar(a.codigo)}</b> <span class="${GRAV[a.gravidade]?.classe ?? ''}">${GRAV[a.gravidade]?.rotulo ?? a.gravidade}</span>
                  ${a.situacao === 'corrigido' ? '<span class="v-ok">corrigido na revisão</span>' : a.situacao === 'parcial' ? '<span class="v-atencao">melhorou, não resolveu</span>' : ''}
                  ${a.reanalisar ? '<span class="selo-inativo">reanalisar na revisão</span>' : ''}
                  <span class="sub-inline">${escapar(a.documento ?? 'entre documentos')}${a.tambem_em ? ` · mantido em ${escapar(a.tambem_em)}` : ''}</span><br>${escapar(a.erro)}</summary>
                <div class="dica" style="margin:.5rem 0 0 1rem">
                  ${a.norma ? `<p><b>Norma ou lógica:</b> ${escapar(a.norma)}</p>` : ''}
                  ${a.correcao_sugerida ? `<p><b>O que pedir ao fornecedor:</b> ${escapar(a.correcao_sugerida)}</p>` : ''}
                  ${a.acao_erp ? `<p><b>O que o sistema faz:</b> ${escapar(a.acao_erp)}</p>` : ''}
                  ${a.situacao_obs ? `<p><b>No PCMSO de 2026:</b> ${escapar(a.situacao_obs)}</p>` : ''}
                </div></details>`,
            )
            .join('')
        : `<p class="dica">Nenhum achado com esse filtro.</p>`;
    };

    secao.innerHTML = `
      <section class="ped-painel"><h3>⚖️ A regra em vigor</h3>
        <p class="dica" style="max-width:none">${escapar(regra?.obs ?? 'A lei prevalece sobre os documentos até a revisão chegar.')}</p>
        <p class="dica" style="max-width:none">Marcados para reanálise quando os documentos novos chegarem: <b>${d.reanalise.exames}</b> exigências de exame,
          <b>${d.reanalise.treinamentos}</b> linhas de treinamento vindas do PGR, <b>${d.reanalise.ghes}</b> GHEs e <b>${d.achados.filter((a) => a.reanalisar).length}</b> achados da auditoria.</p>
      </section>

      <section class="ped-painel"><h3>📑 Documentos da empresa <span class="sub-inline">${auditaveis.length}</span></h3>
        <p class="dica">Auditados em 10/09/2026. “Com ressalvas” quer dizer: vale onde não contraria a lei, e tudo o que o sistema tirou dele será conferido de novo na revisão.</p>
        <div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Documento</th><th>Responsável</th><th>Vigência</th><th>Auditoria</th><th>Atenção</th></tr></thead><tbody>
          ${auditaveis
            .map(
              (x) => `<tr><td class="sst-nome">${escapar(x.titulo)}<br><span class="sub-inline">${escapar(x.fornecedor ?? '')}</span></td>
                <td>${escapar(x.responsavel ?? '—')}<br><span class="sub-inline">${escapar(x.registro ?? '')}</span></td>
                <td class="data">${dataBr(x.vigencia_inicio)} a ${x.vigencia_fim ? dataBr(x.vigencia_fim) : 'sem data'}</td>
                <td><b class="${x.auditoria_situacao === 'com_ressalvas' ? 'v-vencido' : ''}">${SITUACAO[x.auditoria_situacao] ?? x.auditoria_situacao}</b>
                  ${x.achados_abertos ? `<br><span class="sub-inline">${x.achados_abertos} achado(s) em aberto, ${x.achados_altos} de gravidade alta</span>` : ''}
                  ${x.reanalisar_na_revisao ? '<br><span class="selo-inativo">reanalisar quando a revisão chegar</span>' : ''}</td>
                <td>${alertas(x)}</td></tr>`,
            )
            .join('')}
        </tbody></table></div>
      </section>

      <section class="ped-painel"><h3>🎓 Matriz legal de treinamentos <span class="sub-inline">${d.matriz.length} linhas</span></h3>
        <p class="dica">A matriz do PGR tem só 4 treinamentos. O que ele omite entra aqui com procedência “norma”. Quem está exposto é decidido pela lei primeiro:
          se o inventário do GHE tem o risco, todos do GHE contam como expostos até o técnico dizer o contrário. As pendências de treinamento começam
          a aparecer quando houver onde registrar os certificados.</p>
        <div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Treinamento</th><th>Quem</th><th class="num">Ativos expostos</th><th>Carga e validade</th><th>Procedência</th></tr></thead><tbody>
          ${d.matriz
            .map(
              (m) => `<tr><td class="sst-nome">${escapar(m.nome)}<br><span class="sub-inline">${escapar(m.fonte ?? '')}${m.conferido_em ? ` · conferido em ${dataBr(m.conferido_em)}` : ' · <b>não conferido</b>'}</span></td>
                <td>${escapar(m.alvo)}${m.exigencia === 'condicional' ? ' <span class="sub-inline">(condicional)</span>' : ''}</td>
                <td class="num">${m.expostos}</td>
                <td class="data">${m.carga_inicial_h ? `${m.carga_inicial_h} h` : '—'}${m.carga_periodica_h ? ` · periódico ${m.carga_periodica_h} h` : ''}<br>${m.validade_meses ? `vale ${m.validade_meses} meses${m.validade_por_norma ? '' : ' (pelo PGR)'}` : 'só vence por evento'}</td>
                <td><b class="${m.doc_tipo === 'norma' ? 'v-ok' : ''}">${m.doc_tipo === 'norma' ? 'norma' : 'PGR rev. 02'}</b>${m.reanalisar ? ' <span class="selo-inativo">reanalisar</span>' : ''}<br><span class="sub-inline">${escapar(m.procedencia)}</span></td></tr>`,
            )
            .join('')}
        </tbody></table></div>
      </section>

      <section class="ped-painel"><h3>🔎 Achados da auditoria <span class="sub-inline">${d.achados.length}</span></h3>
        <p class="dica">É a pauta para cobrar dos fornecedores antes da revisão de 31/10/2026. Clique num achado para ver a norma, o que pedir e o que o sistema faz enquanto isso.</p>
        <div class="barra-busca">
          <input id="sst-busca" type="search" placeholder="Buscar nos achados…" autocomplete="off">
          <select id="sst-fdoc"><option value="">Todos os documentos</option>${auditaveis.map((x) => `<option>${escapar(x.titulo)}</option>`).join('')}<option value="entre">Entre documentos</option></select>
          <select id="sst-fgrav"><option value="">Toda gravidade</option><option value="alta">Alta</option><option value="media">Média</option><option value="baixa">Baixa</option></select>
        </div>
        <div id="sst-achados"></div>
      </section>`;
    secao.querySelector<HTMLInputElement>('#sst-busca')!.addEventListener('input', (e) => {
      busca = (e.target as HTMLInputElement).value.trim().toUpperCase();
      pintaAchados();
    });
    secao.querySelector('#sst-fdoc')!.addEventListener('change', pintaAchados);
    secao.querySelector('#sst-fgrav')!.addEventListener('change', pintaAchados);
    pintaAchados();
  }

  // -------------------------------------------------------------- funcionários
  function situacaoAso(a: Ativo, hoje: string): { texto: string; classe: string; ordem: number } {
    if (!a.aso_data) return { texto: 'sem ASO no portal', classe: a.afastado ? '' : 'v-vencido', ordem: a.afastado ? 5 : 1 };
    if (!a.aso_validade) return { texto: `exame em ${dataBr(a.aso_data)}, sem validade`, classe: 'v-atencao', ordem: 3 };
    const faltam = dias(hoje, a.aso_validade);
    if (faltam < 0) return { texto: `vencido em ${dataBr(a.aso_validade)}`, classe: a.afastado ? '' : 'v-vencido', ordem: a.afastado ? 5 : 0 };
    if (faltam <= 30) return { texto: `vence em ${dataBr(a.aso_validade)} (${faltam} d)`, classe: 'v-atencao', ordem: 2 };
    return { texto: `em dia até ${dataBr(a.aso_validade)}`, classe: 'v-ok', ordem: 4 };
  }

  function secaoFuncionarios(secao: HTMLElement, d: Painel): void {
    const pinta = (): void => {
      const lista = d.ativos
        .filter((a) => !busca || `${a.nome} ${a.funcao ?? ''} ${a.obra ?? ''}`.toUpperCase().includes(busca))
        .map((a) => ({ a, s: situacaoAso(a, d.hoje) }))
        .sort((x, y) => x.s.ordem - y.s.ordem || x.a.nome.localeCompare(y.a.nome));
      alvo.innerHTML = `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr>
          <th>Funcionário</th><th>Função · GHE</th><th>Obra</th><th>Último ASO</th><th>Situação</th><th class="num">ASOs</th></tr></thead><tbody>
        ${lista
          .map(
            ({ a, s }) => `<tr><td><button class="sst-link" type="button" data-ficha="${a.id}" title="Abrir a ficha">${escapar(a.nome)}</button>${a.afastado ? ` <span class="selo-inativo">afastado: ${escapar(CAUSA[a.afastado] ?? a.afastado)}</span>` : ''}${a.cnh_categoria ? ` <span class="sub-inline">CNH ${escapar(a.cnh_categoria)}</span>` : ''}</td>
              <td>${escapar(a.funcao ?? '—')}${a.ghe ? ` <span class="sub-inline">GHE ${escapar(a.ghe)} · ${escapar(a.ghe_nome ?? '')}</span>` : ''}</td>
              <td class="comp">${escapar(a.obra ?? '—')}</td>
              <td class="data">${a.aso_data ? `${dataBr(a.aso_data)} · ${escapar(a.aso_tipo ?? '')}` : '—'}</td>
              <td class="${s.classe}">${escapar(s.texto)}</td>
              <td class="num">${a.asos}</td></tr>`,
          )
          .join('')}
        </tbody></table></div>`;
    };
    secao.innerHTML = `<p class="dica">Os ${d.ativos.length} ativos no Domínio, com o último ASO que o portal da ASMETRO mostra. Ordenado do mais urgente para o mais folgado. Clique no nome para abrir a ficha.</p>
      ${campoBusca(secao, 'Buscar por nome, função ou obra…', () => pinta())}<div id="sst-lista"></div>
      <section class="ped-painel" style="margin-top:1rem"><h3>🔎 Buscar no cadastro completo</h3>
        <p class="dica">Ativos <b>e desligados</b> — para abrir a ficha de quem já saiu (a lei manda guardar o prontuário por 20 anos). A busca de cima só vê os ativos (03/11, Lucas).</p>
        <div class="barra-busca"><input id="sst-busca-todos" type="search" placeholder="Digite parte do nome…" autocomplete="off"></div>
        <div id="sst-busca-todos-res"></div>
      </section>`;
    const alvo = secao.querySelector<HTMLElement>('#sst-lista')!;
    pinta();

    const entrada = secao.querySelector<HTMLInputElement>('#sst-busca-todos')!;
    const res = secao.querySelector<HTMLElement>('#sst-busca-todos-res')!;
    let relogio = 0;
    entrada.addEventListener('input', () => {
      window.clearTimeout(relogio);
      relogio = window.setTimeout(async () => {
        const q = entrada.value.trim();
        if (q.length < 2) return void (res.innerHTML = '');
        try {
          const r = await api.get<{ funcionarios: Achado[] }>(`/api/sst/funcionarios?q=${encodeURIComponent(q)}&situacao=todos`);
          res.innerHTML = r.funcionarios.length
            ? `<ul class="sst-lista-miuda">${r.funcionarios
                .map((a) => `<li><button class="sst-link" type="button" data-ficha="${a.id}">${escapar(a.nome)}</button> <span class="sub-inline">${escapar(a.funcao ?? '')} · nasc. ${dataBr(a.nascimento)} · ${a.desligamento ? `<span class="v-vencido">saiu em ${dataBr(a.desligamento)}</span>` : 'ativo'}${a.admissao ? ` · admitido ${dataBr(a.admissao)}` : ''}</span></li>`)
                .join('')}</ul>`
            : '<p class="dica">Ninguém com esse nome no cadastro do Domínio.</p>';
        } catch {
          res.innerHTML = '<p class="dica">A busca falhou.</p>';
        }
      }, 300);
    });
  }

  // ------------------------------------------------------- nomes sem vínculo
  function secaoFila(secao: HTMLElement, d: Painel): void {
    const pinta = (): void => {
      const lista = d.fila.filter((f) => !busca || `${f.nome_portal} ${f.funcao ?? ''}`.toUpperCase().includes(busca));
      alvo.innerHTML = !lista.length
        ? `<p class="dica">Nenhum nome esperando vínculo. 👌</p>`
        : // Os botões ficam EMBAIXO do nome (pedido do Lucas, 18/09): numa coluna própria, o nome comprido
          // da sugestão não quebrava e a tabela ganhava barra de rolagem horizontal.
          `<div class="rolagem"><table class="tabela sst-tabela"><thead><tr>
            <th>Nome no portal</th><th>Função</th><th>Nascimento</th><th class="num">Exames</th><th>Período</th></tr></thead><tbody>
          ${lista
            .map(
              (f) => `<tr data-linha="${f.id}"><td><span class="sst-nome">${escapar(f.nome_portal)}</span>${f.nascimento ? ` <span class="selo-inativo">atual</span>` : ''}${f.motivo === 'homonimo' ? ` <span class="sub-inline">homônimo no cadastro</span>` : ''}
                  <div class="sst-acoes">
                    ${f.sugestao_funcionario_id ? `<button class="btn btn-claro" data-vincular="${f.id}" data-func="${f.sugestao_funcionario_id}">É ${escapar(f.sugestao_nome ?? '')}${f.sugestao_nascimento ? ` (${dataBr(f.sugestao_nascimento)})` : ''}</button>` : ''}
                    <button class="btn btn-claro" data-escolher="${f.id}">Escolher…</button>
                  </div>
                  <div data-busca-de="${f.id}"></div></td>
                <td>${escapar(f.funcao ?? '—')}</td><td class="data">${dataBr(f.nascimento)}</td><td class="num">${f.exames}</td>
                <td class="data">${f.primeiro_exame ? `${dataBr(f.primeiro_exame)}<br>a ${dataBr(f.ultimo_exame)}` : '—'}</td></tr>`,
            )
            .join('')}
          </tbody></table></div>`;
    };
    secao.innerHTML = `<p class="dica">O portal da ASMETRO não tem CPF. O que não casou por nome idêntico fica aqui até alguém dizer quem é —
        quase tudo é ex-funcionário com grafia diferente. Depois de vincular, clique em <b>Reprocessar</b> (aba Cargas) ou espere a carga das 06:30:
        os exames daquele nome entram sozinhos. O botão “É …” aparece quando primeiro e último nome batem com uma pessoa só.</p>
      ${campoBusca(secao, 'Buscar nome ou função…', () => pinta())}<div id="sst-lista"></div>`;
    const alvo = secao.querySelector<HTMLElement>('#sst-lista')!;
    pinta();

    const vincular = async (filaId: number, funcionarioId: number): Promise<void> => {
      const linha = d.fila.find((f) => f.id === filaId);
      if (!linha) return;
      try {
        const r = await api.post<{ funcionario: string; exames: number }>('/api/sst/vinculos/asmetro', { nomeNormalizado: linha.nome_normalizado, funcionarioId });
        avisar(`${linha.nome_portal} agora é ${r.funcionario}. ${r.exames} exame(s) entram na próxima carga.`);
        d.fila = d.fila.filter((f) => f.id !== filaId);
        desenhar();
      } catch (erro) {
        avisar(erro instanceof ErroApi ? erro.message : 'Não consegui vincular.', 'atencao');
      }
    };

    alvo.addEventListener('click', (e) => {
      const alvoClique = e.target as HTMLElement;
      const direto = alvoClique.closest<HTMLElement>('[data-vincular]');
      if (direto) return void vincular(Number(direto.dataset['vincular']), Number(direto.dataset['func']));
      const achado = alvoClique.closest<HTMLElement>('[data-achado]');
      if (achado) return void vincular(Number(achado.dataset['fila']), Number(achado.dataset['achado']));
      const escolher = alvoClique.closest<HTMLElement>('[data-escolher]');
      if (!escolher) return;
      const id = Number(escolher.dataset['escolher']);
      const caixa = alvo.querySelector<HTMLElement>(`[data-busca-de="${id}"]`)!;
      caixa.innerHTML = `<input type="search" placeholder="Digite parte do nome no cadastro…" style="width:100%;margin-top:.4rem;padding:.4rem .5rem;font:inherit"><div class="sub-inline" data-res></div>`;
      const entrada = caixa.querySelector('input')!;
      const res = caixa.querySelector<HTMLElement>('[data-res]')!;
      let relogio = 0;
      entrada.addEventListener('input', () => {
        window.clearTimeout(relogio);
        relogio = window.setTimeout(async () => {
          const q = entrada.value.trim();
          if (q.length < 2) return void (res.innerHTML = '');
          try {
            const r = await api.get<{ funcionarios: Achado[] }>(`/api/sst/funcionarios?q=${encodeURIComponent(q)}`);
            res.innerHTML = r.funcionarios.length
              ? r.funcionarios
                  .map((a) => `<div style="margin:.25rem 0"><button class="btn btn-claro" data-achado="${a.id}" data-fila="${id}">${escapar(a.nome)}</button> ${escapar(a.funcao ?? '')} · nasc. ${dataBr(a.nascimento)} · ${a.desligamento ? `saiu em ${dataBr(a.desligamento)}` : 'ativo'}</div>`)
                  .join('')
              : 'Ninguém com esse nome no cadastro do Domínio.';
          } catch {
            res.textContent = 'A busca falhou.';
          }
        }, 300);
      });
      entrada.focus();
    });
  }

  // -------------------------------------------------------------------- cargas
  function secaoCargas(secao: HTMLElement, d: Painel): void {
    const linha = (nome: string, quandoFoi: { em: string } | null, como: string): string =>
      `<tr><td class="sst-nome">${nome}</td><td>${quandoFoi ? escapar(quando(quandoFoi.em)) : '<span class="v-vencido">nunca rodou</span>'}</td><td class="comp">${como}</td></tr>`;
    secao.innerHTML = `
      <section class="ped-painel"><h3>De onde vêm os dados</h3>
        <p class="dica">Ninguém digita nada aqui: o cadastro vem da folha e os exames vêm da clínica. O relógio compara com a norma e o PCMSO e monta as pendências.</p>
        <div class="rolagem"><table class="tabela sst-tabela"><thead><tr><th>Carga</th><th>Última vez</th><th>Como</th></tr></thead><tbody>
          ${linha('Cadastro do Domínio', d.cargas.dominio, 'a estação do RH extrai e envia todo dia às 12:00 (somente leitura no Domínio)')}
          ${linha('Portal da ASMETRO', d.cargas.asmetro, 'o sistema lê os relatórios do portal todo dia às 06:30')}
          ${linha('Relógio das pendências', d.cargas.relogio, 'roda depois de cada carga')}
        </tbody></table></div>
        <p style="margin-top:1rem"><button class="btn" id="sst-reprocessar">🔄 Reprocessar agora</button>
          <span class="sub-inline">lê o portal da ASMETRO e recalcula as pendências (uns 10 segundos)</span></p>
      </section>`;
    secao.querySelector<HTMLButtonElement>('#sst-reprocessar')!.addEventListener('click', async (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      b.disabled = true;
      b.textContent = 'Reprocessando…';
      try {
        const r = await api.post<{ asmetro: { asosNovos: number; nomesSemVinculo: number }; relogio: { abertas: number; novas: number; resolvidas: number } }>('/api/sst/reprocessar', {});
        avisar(`${r.asmetro.asosNovos} ASO(s) novo(s). Pendências: ${r.relogio.abertas} abertas, ${r.relogio.novas} novas, ${r.relogio.resolvidas} resolvidas.`);
        await carregar();
      } catch (erro) {
        avisar(erro instanceof ErroApi ? erro.message : 'Não consegui reprocessar.', 'atencao');
        b.disabled = false;
        b.textContent = '🔄 Reprocessar agora';
      }
    });
  }

  void carregar();
}
